// ─────────────────────────────────────────────────────────────────
// Glass-i — Bank Reconciliation pure helpers (Stage 6E.1).
//
// Pure JS — no React, no AntD, no axios. Designed for node:test.
// ─────────────────────────────────────────────────────────────────

export const RECONCILIATION_STATUS_COLOR = {
  draft:       'default',
  in_progress: 'processing',
  reconciled:  'green',
  locked:      'blue',
  cancelled:   'red'
};
export const RECONCILIATION_STATUS_LABEL = {
  draft:       'Draft',
  in_progress: 'In progress',
  reconciled:  'Reconciled',
  locked:      'Locked',
  cancelled:   'Cancelled'
};

export const CHEQUE_STATUS_COLOR = {
  pending:   'default',
  issued:    'processing',
  deposited: 'blue',
  cleared:   'green',
  bounced:   'red',
  cancelled: 'red',
  stopped:   'volcano'
};
export const CHEQUE_STATUS_LABEL = {
  pending:   'Pending',
  issued:    'Issued',
  deposited: 'Deposited',
  cleared:   'Cleared',
  bounced:   'Bounced',
  cancelled: 'Cancelled',
  stopped:   'Stopped'
};

export const MATCH_STATUS_COLOR = {
  unmatched: 'default',
  matched:   'green',
  partial:   'gold',
  ignored:   'volcano',
  disputed:  'red'
};
export const MATCH_STATUS_LABEL = {
  unmatched: 'Unmatched',
  matched:   'Matched',
  partial:   'Partial',
  ignored:   'Ignored',
  disputed:  'Disputed'
};

// Same transition table as the backend (bankReconciliation.constants).
// Front-end mirrors it for instant pre-submit UX feedback. If the
// backend ever loosens or tightens this, update both sides.
export const CHEQUE_TRANSITIONS = {
  pending:   ['issued', 'deposited', 'cancelled', 'stopped'],
  issued:    ['deposited', 'cancelled', 'stopped'],
  deposited: ['cleared', 'bounced'],
  cleared:   [],
  bounced:   [],
  cancelled: [],
  stopped:   []
};

export function canTransitionChequeStatus(fromStatus, toStatus) {
  if (!fromStatus) {
    return ['pending', 'issued', 'deposited', 'cleared', 'bounced',
            'cancelled', 'stopped'].includes(toStatus);
  }
  const allowed = CHEQUE_TRANSITIONS[fromStatus];
  if (!allowed) return false;
  return allowed.includes(toStatus);
}

export function classifyDebitCredit(line) {
  if (!line) return null;
  const dr = Number(line.debitAmount)  || 0;
  const cr = Number(line.creditAmount) || 0;
  if (dr > 0 && cr === 0) return 'debit';
  if (cr > 0 && dr === 0) return 'credit';
  return null;
}

export function calculateStatementLineAmount(line) {
  if (!line) return 0;
  const dr = Number(line.debitAmount)  || 0;
  const cr = Number(line.creditAmount) || 0;
  return dr + cr;
}

export function computeDifference(statementClosing, bookClosing) {
  const s = Number(statementClosing) || 0;
  const b = Number(bookClosing)      || 0;
  return Math.round((s - b) * 100) / 100;
}

export function formatBRSAmount(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.00';
  return v.toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

export function chequeStatusLabel(status) {
  return CHEQUE_STATUS_LABEL[status] || status || '—';
}
export function matchStatusLabel(status) {
  return MATCH_STATUS_LABEL[status] || status || '—';
}
export function reconciliationStatusLabel(status) {
  return RECONCILIATION_STATUS_LABEL[status] || status || '—';
}

// ── Payload builders ───────────────────────────────────────────
export function buildStatementLinePayload(form) {
  if (!form) return {};
  const dr = Number(form.debitAmount)  || 0;
  const cr = Number(form.creditAmount) || 0;
  return {
    statementDate: form.statementDate || null,
    valueDate:     form.valueDate || null,
    description:   form.description || null,
    referenceNo:   form.referenceNo || null,
    chequeNo:      form.chequeNo || null,
    debitAmount:   dr > 0 ? dr : 0,
    creditAmount:  cr > 0 ? cr : 0,
    balanceAfter:  form.balanceAfter !== '' && form.balanceAfter !== null
                    && form.balanceAfter !== undefined
                    ? Number(form.balanceAfter) : null,
    remarks:       form.remarks || null
  };
}

export function buildMatchPayload({ statementLineId, voucherLineId,
                                     matchedAmount, matchType, matchNote }) {
  return {
    statementLineId,
    voucherLineId,
    matchedAmount: Number(matchedAmount) || 0,
    matchType:     matchType || 'manual',
    matchNote:     matchNote || null
  };
}

export function buildReconciliationQuery({ bankAccountId, status,
                                            fromDate, toDate, page, pageSize } = {}) {
  const q = {};
  if (bankAccountId) q.bankAccountId = bankAccountId;
  if (status) q.status = status;
  if (fromDate) q.fromDate = fromDate;
  if (toDate)   q.toDate   = toDate;
  if (page)     q.page     = page;
  if (pageSize) q.pageSize = pageSize;
  return q;
}

export function buildChequeEventPayload({ newStatus, eventDate,
                                            bankAccountId, referenceNo,
                                            bounceReason, remarks }) {
  return {
    newStatus,
    eventDate:     eventDate || null,
    bankAccountId: bankAccountId || null,
    referenceNo:   referenceNo || null,
    bounceReason:  bounceReason || null,
    remarks:       remarks || null
  };
}

// ── Form-side validators (return array of field-message tuples) ──
export function findStatementLineFormErrors(form) {
  const errs = [];
  if (!form) return errs;
  if (!form.statementDate) errs.push(['statementDate', 'Statement date is required.']);
  const dr = Number(form.debitAmount)  || 0;
  const cr = Number(form.creditAmount) || 0;
  if (dr === 0 && cr === 0) {
    errs.push(['debitAmount', 'Either debit or credit amount must be greater than zero.']);
  }
  if (dr > 0 && cr > 0) {
    errs.push(['debitAmount', 'A statement line cannot carry both debit and credit amounts.']);
  }
  if (dr < 0) errs.push(['debitAmount',  'Debit amount cannot be negative.']);
  if (cr < 0) errs.push(['creditAmount', 'Credit amount cannot be negative.']);
  return errs;
}

export function findReconciliationFormErrors(form) {
  const errs = [];
  if (!form) return errs;
  if (!form.bankAccountId)     errs.push(['bankAccountId', 'Bank account is required.']);
  if (!form.statementFromDate) errs.push(['statementFromDate', 'Start date is required.']);
  if (!form.statementToDate)   errs.push(['statementToDate',   'End date is required.']);
  if (form.statementFromDate && form.statementToDate
      && form.statementToDate < form.statementFromDate) {
    errs.push(['statementToDate', 'End date must be on or after start date.']);
  }
  return errs;
}

export function friendlyChequeErrorMessage(err) {
  const body = err && err.response && err.response.data
                && err.response.data.error;
  if (body && body.message) return body.message;
  if (err && err.message)   return err.message;
  return 'Could not update cheque status. Please try again.';
}
